import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUnlock,
  faLock,
  faEye,
  faSitemap,
  faArrowRight,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/services/api";
import { API_ENDPOINTS } from "@/config/api";
import { formatAmount } from "@/utils/constants";


import UserSettingsModal from "@/components/UserSettingsModal"; // NEW: Import the modal
import { socketService } from "@/services/socket";

export default function AgentList() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [showExtraColumns, setShowExtraColumns] = useState(false);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null); // Keeping for now if needed, but likely unused later

  // NEW: State for standalone Credit Modal
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);
  const [selectedCreditAgent, setSelectedCreditAgent] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    totalPages: 1,
    totalWallets: 0
  });

  const [currentParentId, setCurrentParentId] = useState(null); // NEW: Track current parent
  // Keep track of navigation history for "Back" functionality
  const [navigationStack, setNavigationStack] = useState([]);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      // Endpoint: /api/admin/users/all-details?page=1&limit=25&search=&parentId=...
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm
      };

      if (currentParentId) {
        params.parentId = currentParentId;
      }

      const response = await api.get(API_ENDPOINTS.USER.ALL_DETAILS, { params });

      if (response.data && response.data.success && response.data.data) {
        const { wallets, pagination: apiPagination } = response.data.data;

        // Map API data to component state structure
        // Map API data to component state structure
        const mappedAgents = wallets.map(wallet => ({
          id: wallet.wallet_id,
          // stored IDs for logic
          user_id: wallet.user_id,
          staff_id: wallet.staff_id,
          user_type: wallet.user_type, // Added
          role: wallet.role, // Added

          currentPercentage: wallet.percentage || 0, // NEW
          netWin: parseFloat(wallet.net_win || 0),   // NEW
          netLoss: parseFloat(wallet.net_loss || 0), // NEW
          profit: parseFloat(wallet.profit || 0),    // NEW

          loginName: wallet.username,
          accountType: wallet.user_type || wallet.role, // Use user_type or role
          downline: wallet.downline, // Dynamic from API
          bet_locked: wallet.bet_locked, // NEW: Pass explicit lock status which userSettingsModal relies on
          expLimit: wallet.exp_limit !== null ? parseFloat(wallet.exp_limit) : null, // NEW: Exposure Limit
          bettingStatus: !wallet.bet_locked ? "unlocked" : "locked",
          status: wallet.status, // "Active" or "InActive"
          netExposure: parseFloat(wallet.exposure || 0),
          gt: parseFloat(wallet.GT || 0), // Mapping GT from API
          creditLimit: parseFloat(wallet.credit_limit || 0),
          availableCredit: parseFloat(wallet.credit || 0), // Assuming inr_balance is available credit
          created: new Date(wallet.createdAt).toLocaleString(),
          lastLogin: new Date(wallet.updatedAt).toLocaleString(), // Using updatedAt as proxy for last login
        }));

        setAgents(mappedAgents);
        setPagination(prev => ({
          ...prev,
          totalPages: apiPagination.total_pages,
          totalWallets: apiPagination.total_wallets
        }));
      }
    } catch (error) {
      console.error("Error fetching agent listing:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount and when pagination/search/currentParentId changes
  useEffect(() => {
    // 1. Initial API Fetch (Fallback & Fast First Paint)
    const timer = setTimeout(() => {
      fetchAgents();
    }, 500);

    // 2. Socket Subscription for Real-time Updates
    const token = localStorage.getItem('token') || localStorage.getItem('ownerToken') || localStorage.getItem('staffToken');
    let unsubscribe = null;

    if (token) {
      // Ensure connected (idempotent)
      socketService.connect(token);

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        parentId: currentParentId
      };

      unsubscribe = socketService.subscribeToUsersAllDetails(params, token, (data) => {
        if (data && data.success && data.data) {
          const { wallets, pagination: apiPagination } = data.data;

          // Map logic same as fetchAgents
          const mappedAgents = wallets.map(wallet => ({
            id: wallet.wallet_id,
            user_id: wallet.user_id,
            staff_id: wallet.staff_id,
            user_type: wallet.user_type,
            role: wallet.role,
            currentPercentage: wallet.percentage || 0,
            netWin: parseFloat(wallet.net_win || 0),
            netLoss: parseFloat(wallet.net_loss || 0),
            profit: parseFloat(wallet.profit || 0),
            loginName: wallet.username,
            accountType: wallet.user_type || wallet.role,
            downline: wallet.downline,
            bet_locked: wallet.bet_locked,
            expLimit: wallet.exp_limit !== null ? parseFloat(wallet.exp_limit) : null,
            bettingStatus: !wallet.bet_locked ? "unlocked" : "locked",
            status: wallet.status,
            netExposure: parseFloat(wallet.exposure || 0),
            gt: parseFloat(wallet.GT || 0),
            creditLimit: parseFloat(wallet.credit_limit || 0),
            availableCredit: parseFloat(wallet.credit || 0),
            created: new Date(wallet.createdAt).toLocaleString(),
            lastLogin: new Date(wallet.updatedAt).toLocaleString(),
          }));

          setAgents(mappedAgents);
          setPagination(prev => ({
            ...prev,
            totalPages: apiPagination.total_pages,
            totalWallets: apiPagination.total_wallets
          }));
          setLoading(false); // Ensure loading is false when data flows
        }
      });
    }

    return () => {
      clearTimeout(timer);
      if (unsubscribe) unsubscribe();
    };
  }, [pagination.page, searchTerm, currentParentId]);

  const handleDownloadCSV = () => {
    // Define the headers matching the image provided
    const headers = [
      "Login Name",
      "Account Type",
      "Betting Status",
      "Status",
      "Net Exposure",
      "G/T",
      "Credit Limit",
      "Available Credit",
      "Created",
      "Last Login"
    ];

    // Map the agents data to rows
    const rows = agents.map(agent => [
      agent.loginName,
      agent.accountType,
      agent.bettingStatus === "unlocked" ? "TRUE" : "FALSE", // Mapping to match image style
      agent.status === "Active" ? "TRUE" : "FALSE",         // Assumption based on image, fallback to valid truthy/falsy
      agent.netExposure,
      agent.gt,
      agent.creditLimit,
      agent.availableCredit,
      agent.created,
      agent.lastLogin
    ]);

    // Combine headers and rows into a CSV string
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(item => `"${String(item).replace(/"/g, '""')}"`).join(",")) // Quote items to handle commas in data
    ].join("\n");

    // Create a Blob and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `agent_listing_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleNewAgent = () => {
    navigate("/createagent");
  };

  const handleViewDetails = (agent) => {
    // Determine the correct ID to navigate with
    const targetId = agent.user_type === 'USER' ? agent.user_id : agent.staff_id;
    console.log("Viewing details for agent:", targetId, agent.role);
    navigate(`/admin/user-details/${targetId}`, { state: { username: agent.loginName, type: agent.user_type, role: agent.role, walletId: agent.id } });
  };

  // NEW: Handle Downline Click
  const handleDownlineClick = (agent) => {
    // Logic: If user has downline (checked via flag), drill down.
    // We need the ID to drill down into. 
    // If it's staff, use staff_id. If user (rare but possible logic), use user_id?
    // Actually backend expects `parentId`.
    // The `parentId` filtering logic checks `parent_id` (Staff) or `parent_staff_id` (User).
    // So if we click on a Staff row, we pass its `staff_id` as `parentId`.

    const targetId = agent.staff_id || agent.user_id; // Prefer staff_id as parents are usually staff

    if (agent.downline) {
      setNavigationStack(prev => [...prev, currentParentId]); // Push current to stack
      setCurrentParentId(targetId);
      setPagination(prev => ({ ...prev, page: 1 })); // Reset page
      setSearchTerm(""); // Clear search on drill down? Or keep? usually clear is better context switch
    }
  };

  const handleBack = () => {
    if (navigationStack.length > 0) {
      const prevId = navigationStack[navigationStack.length - 1];
      setNavigationStack(prev => prev.slice(0, -1));
      setCurrentParentId(prevId);
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  };

  const toggleExtraColumns = () => {
    setShowExtraColumns((prev) => !prev);
  };

  return (
    <Layout>
      <div className="p-4 w-full mx-auto bg-[#171b36] min-h-screen">
        {/* Header Section */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          className="shadow-sm px-4 pt-4"
        >
          <div className="flex justify-between items-start">
            <div className="flex items-center justify-between space-x-4 w-full mb-1">
              <div>
                <div
                  style={{ color: "#cae0e8", fontSize: "24px" }}
                  className="font-semibold mb-1 flex items-center gap-2"
                >
                  {currentParentId && (
                    <button onClick={handleBack} className="text-sm bg-gray-700 px-2 py-1 rounded hover:bg-gray-600 transition">
                      <FontAwesomeIcon icon={faArrowLeft} /> Back
                    </button>
                  )}
                  Agent Listing
                </div>
              </div>

              <div
                style={{}}
                className="flex items-center space-x-2"
              >
                <label className="text-white text-[14px] font-bold mr-2">
                  Search By User Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 on search
                    }}
                    placeholder="Enter Atleast 3 character"
                    className="px-2 py-2 w-[180px] text-[11px] bg-gray-200 text-black placeholder-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleDownloadCSV}
                  className="bg-[#11374a] text-white font-bold text-[14px] px-2 py-1 transition-colors"
                >
                  Download CSV
                </button>
                <button
                  onClick={handleNewAgent}
                  className="px-4 py-2 bg-[#333d79ff] text-white font-bold text-[14px] transition-colors"
                >
                  New Agent
                </button>
              </div>
            </div>
          </div>
          <div className="w-full h-px bg-white mb-2"></div>
          <h2
            style={{ color: "white", fontStyle: "italic" }}
            className="text-[14px] font-bold text-white text-left"
          >
            Downline Listing {currentParentId ? `(Level ID: ${currentParentId})` : ''}
          </h2>
        </div>

        {/* Table Section - Table shrinks to fit */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)", marginTop: "-8px", minHeight: '500px' }}
          className="shadow-sm p-4 flex"
        >
          {/* Table Container - Shrinks and distributes space */}
          <div className="flex-1 min-w-0">
            <div className="overflow-hidden">
              <table className="w-full text-sm table-fixed">
                <thead>
                  <tr>
                    <th className="text-left px-4 py-2 text-white font-bold text-[15px] w-[12%]">
                      Login Name
                    </th>
                    <th className="text-left px-4 py-2 text-white font-bold text-[15px] w-[10%]">
                      Account Type
                    </th>
                    <th className="text-center px-4 py-2 text-white font-bold text-[15px] w-[8%]">
                      Downline
                    </th>
                    <th className="text-center px-4 py-2 text-white font-bold text-[15px] w-[10%]">
                      Betting Status
                    </th>
                    <th className="text-center px-4 py-2 text-white font-bold text-[15px] w-[8%]">
                      Status
                    </th>
                    <th className="text-center px-4 py-2 text-white font-bold text-[15px] w-[8%]">
                      Details
                    </th>
                    <th className="text-right px-4 py-2 text-white font-semibold w-[10%]">
                      Net Exposure
                    </th>
                    <th className="text-right px-4 py-2 text-white font-semibold w-[8%]">
                      G/T
                    </th>
                    <th className="text-right px-4 py-2 text-white font-semibold w-[10%]">
                      Credit Limit
                    </th>
                    <th className="text-right px-4 py-2 text-white font-semibold w-[10%]">
                      Available Credit
                    </th>


                    {/* Extra Columns - Appear only when toggled */}
                    {showExtraColumns && (
                      <>
                        <th className="text-center px-4 py-2 text-white font-bold text-[15px] w-[14%]">
                          Created
                        </th>
                        <th className="text-center px-4 py-2 text-white font-bold text-[15px] w-[14%]">
                          Last Login
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={showExtraColumns ? 12 : 10} className="text-center text-white py-4">
                        Loading...
                      </td>
                    </tr>
                  ) : agents.length === 0 ? (
                    <tr>
                      <td colSpan={showExtraColumns ? 12 : 10} className="text-center text-white py-4">
                        No agents found
                      </td>
                    </tr>
                  ) : (
                    agents.map((agent, index) => (
                      <tr
                        key={agent.id}
                        className={`transition-colors ${index % 2 === 0 ? "bg-[rgba(245,245,245,0.1)]" : ""
                          }`}
                      >
                        <td className="py-2 px-4 text-[#2ce5dd] font-medium truncate">
                          <button
                            onClick={() => {
                              if (currentParentId) return; // Prevent editing in downline view
                              setSelectedCreditAgent(agent);
                              setIsCreditModalOpen(true);
                            }}
                            className={`${!currentParentId ? "hover:underline cursor-pointer" : "cursor-default text-[#2ce5dd]"}`}
                          >
                            {agent.loginName}
                          </button>
                        </td>
                        <td className="py-2 px-4 text-white font-bold">
                          {agent.accountType}
                        </td>
                        <td className="py-2 px-4 text-center">
                          <button
                            onClick={() => handleDownlineClick(agent)}
                            disabled={!agent.downline}
                            className={`focus:outline-none transition-colors ${agent.downline ? "cursor-pointer text-white hover:text-blue-400" : "cursor-default text-gray-500 opacity-50"}`}
                          >
                            <FontAwesomeIcon
                              icon={faSitemap}
                              className="h-4 w-4"
                            />
                          </button>
                        </td>
                        <td className="py-2 px-4 text-center">
                          <FontAwesomeIcon
                            icon={agent.bettingStatus === "unlocked" ? faUnlock : faLock}
                            className={`h-4 w-4 ${agent.bettingStatus === "unlocked" ? "text-[#3cd247]" : "text-red-500"}`}
                          />
                        </td>
                        <td className="py-2 px-4 text-center text-white font-bold">
                          {agent.status}
                        </td>
                        <td className="py-2 px-4 text-center">
                          <button
                            onClick={() => handleViewDetails(agent)}
                            className="text-[#2ce5dd] hover:text-blue-300 transition-colors"
                          >
                            <FontAwesomeIcon icon={faEye} className="h-4 w-4" />
                          </button>
                        </td>
                        <td className={`py-2 px-4 text-right font-bold ${agent.netExposure === 0 ? 'text-red-500' : 'text-[#3cd247]'}`}>
                          {formatAmount(Math.abs(agent.netExposure))}
                        </td>
                        <td className={`py-2 px-4 text-right font-bold ${agent.gt >= 0 ? 'text-red-500' : 'text-[#3cd247]'}`}>
                          {formatAmount(agent.gt)}
                        </td>
                        <td className="py-2 px-4 text-right text-white font-bold">
                          <button
                            onClick={() => {
                              if (currentParentId) return; // Prevent editing in downline view
                              setSelectedCreditAgent(agent);
                              setIsCreditModalOpen(true);
                            }}
                            className={`${!currentParentId ? "hover:underline hover:text-blue-400 cursor-pointer" : "cursor-default"}`}
                          >
                            {formatAmount(agent.creditLimit)}
                          </button>
                        </td>
                        <td className="py-2 px-4 text-right text-white font-bold">
                          {formatAmount(agent.availableCredit)}
                        </td>


                        {showExtraColumns && (
                          <>
                            <td className="py-2 px-4 text-center text-white text-[13px] ">
                              {agent.created}
                            </td>
                            <td className="py-2 px-4 text-center text-white text-[13px] ">
                              {agent.lastLogin}
                            </td>
                          </>
                        )}
                      </tr>
                    )))}
                </tbody>
              </table>

              {/* Pagination Controls */}
              {pagination.totalPages > 1 && (
                <div className="flex justify-center mt-4 space-x-2 text-white">
                  <button
                    disabled={pagination.page === 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    className="px-3 py-1 bg-[#333d79ff] rounded disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span>Page {pagination.page} of {pagination.totalPages}</span>
                  <button
                    disabled={pagination.page === pagination.totalPages}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    className="px-3 py-1 bg-[#333d79ff] rounded disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Toggle Sidebar */}
          <div
            style={{ backgroundColor: "rgba(255, 255, 255, 0.2)", marginTop: '10px' }}
            className="w-[44px] bg-[#1f2549] flex-shrink-0 cursor-pointer transition-all duration-300 hover:bg-[#2a3466]"
            onClick={toggleExtraColumns}
          >
            <div style={{ marginTop: '30px' }} className="flex justify-center h-full">
              <FontAwesomeIcon
                icon={showExtraColumns ? faArrowLeft : faArrowRight}
                className="h-5 w-5 text-white"
                style={{
                  fontWeight: 'bold',
                }}

              />
            </div>
          </div>
        </div>
      </div>

      {/* credit modal  */}
      {/* Existing Modal (kept for reference or if used for other things) */}


      {/* User Settings Modal (formerly Credit Modal) */}
      <UserSettingsModal
        isOpen={isCreditModalOpen}
        onClose={() => setIsCreditModalOpen(false)}
        userData={selectedCreditAgent}
        onSave={(data) => {
          console.log("Saving user settings data:", data);
          // TODO: Implement API call here
          setIsCreditModalOpen(false);
        }}
      />


    </Layout>
  );
}